import { useMemo, useEffect, useCallback } from "react";
import { sortByCreatedDesc } from "../../../features/deployments";
import { useFetchDeploymentsBySiteId } from "../../../hooks/useFetchDeployments";
import { shouldDisableDate } from "./deployment-queries";
import { DeploymentNavStorage } from "./storage";
import { useDeploymentNavState } from "./use-deployment-nav-state";
import { useUserContext } from "../../../providers/useUserProvider";
import {
    toTemporalZonedDateTime,
    startOf,
    toDate as temporalToDate,
    addDays,
} from "../../../shared/lib/temporal";

export function useDeploymentNavController(siteId?: string) {
    // Fetch deployments
    const { deployments, isLoading } = useFetchDeploymentsBySiteId(siteId!);
    const timezone = useUserContext().timezoneDetails.timezone;

    // Memoize deployments deeply to avoid unnecessary reference changes
    const stableDeployments = useMemo(() => {
        if (!deployments) return [];
        return sortByCreatedDesc(deployments);
    }, [deployments]);

    const deploymentsReady = useMemo(() => {
        if (!stableDeployments) return false;
        return (
            !isLoading &&
            stableDeployments.every(
                (deployment) => deployment.siteId === siteId,
            )
        );
    }, [siteId, stableDeployments]);

    // Storage management
    const storage = useMemo(() => new DeploymentNavStorage(), []);

    // State management
    const { selectedDate, selectedDeployment, setDate, reset } =
        useDeploymentNavState(stableDeployments, storage, timezone);

    // Compute derived values for the UI
    const { dates } = useMemo(() => {
        if (!deploymentsReady) return { dates: [] };
        const dates: Date[] = [];
        deployments.forEach((deployment) => {
            let d = startOf(
                toTemporalZonedDateTime(
                    new Date(deployment.startTime),
                    timezone,
                ),
                "day",
            );
            const end = startOf(
                toTemporalZonedDateTime(new Date(deployment.endTime), timezone),
                "day",
            );
            while (
                Temporal.PlainDate.compare(
                    d.toPlainDate(),
                    end.toPlainDate(),
                ) <= 0
            ) {
                dates.push(temporalToDate(d));
                d = addDays(d, 1);
            }
        });
        return { dates: dates.sort((a, b) => a.getTime() - b.getTime()) };
    }, [deploymentsReady, stableDeployments]);

    const shouldDisableDateCallback = useCallback(
        (maybeDate: Date | string | number | null) => {
            let date: Date;
            if (maybeDate instanceof Date) {
                date = maybeDate;
            } else if (maybeDate === null) {
                date = new Date(NaN);
            } else {
                date = new Date(maybeDate as string | number);
            }
            const result = shouldDisableDate(date, stableDeployments, timezone);
            return result;
        },
        [stableDeployments, timezone],
    );

    // Detect full page reload using beforeunload event
    // to clear storage on reload. This ensures that
    // on each new page load either today or the latest
    // deployment end date is used as the default date.
    useEffect(() => {
        const handleBeforeUnload = () => {
            reset();
        };
        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, []);

    return {
        selectedDate:
            deploymentsReady && selectedDate !== null
                ? temporalToDate(
                      startOf(
                          toTemporalZonedDateTime(selectedDate, timezone),
                          "day",
                      ),
                  )
                : null,
        selectedDeployment: deploymentsReady ? selectedDeployment : null,
        setSelectedDate: deploymentsReady ? setDate : () => {},
        dates: deploymentsReady ? dates : [],
        shouldDisableDate: shouldDisableDateCallback,
    };
}
